/*
 * AI Usage: Yes - GitHub Copilot
 * This code was generated with assistance from GitHub Copilot.
 */
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { GridColumn } from 'semantic-ui-react'
import RegisteredFormField from '../../common/RegisteredFormField'
import YesNoSelect from '../common/inputs/YesNoSelect'
import DynamicList from '../common/fieldgroups/DynamicList'
import {
  medicalConditionFields,
  medicationFields,
  optionalField,
  requiredField
} from '../../../state/common/FORM_SEGMENTS'
import MedicationInputRow from './MedicationInputRow'
import { useDispatch, useSelector } from 'react-redux'
import { appendList, setField } from '../../../state/slice/form'
import _ from 'lodash'
import HealthQuestionAdditionalInformationRow from './HealthQuestionAdditionalInformationRow'
import { DATE_MASK_OPTIONS } from '../../../constant/masks'

const fullName = (fields) => {
  return [fields?.firstName?.value, fields?.middleInitial?.value, fields?.lastName?.value].filter(Boolean).join(' ')
}

const HealthQuestion = ({
  id,
  healthQuestion,
  rootPath,
  applicants,
  disableAdditionalInfo = false,
  disableMedication = false,
  segmentPath = null,
}) => {
  const dispatch = useDispatch()

  const employeeApplicantFields = useSelector(state => state.form.segments.employee.fields)
  const spouseApplicantFields = useSelector(state => state.form.segments.dependents.fields.spouse)

  const applicantFullName = (applicantValue) => {
    switch (applicantValue) {
      case 'employee':
        return fullName(employeeApplicantFields)
      case 'spouse':
        return fullName(spouseApplicantFields)
      default:
        return ''
    }
  }

  const isApplicantSelectedYes = (applicant) => {
    return healthQuestion.type === 'choice' ?
      Object.keys(healthQuestion.choices).some(c => healthQuestion.choices[c][applicant.value]?.value) :
      healthQuestion[applicant.value]?.value === 'yes'
  }

  const anyApplicantSelectedYes = Array.of('employee', 'spouse', 'children').some(a => {
    return healthQuestion.type === 'choice' ?
      Object.keys(healthQuestion.choices).some(c => healthQuestion.choices[c][a]?.value) :
      healthQuestion[a]?.value === 'yes'
  })

  const syncApplicantSpecificList = (list, defaultFields, listPath, countPath) => {
    const applicantsSelectedYes = applicants.filter(isApplicantSelectedYes)
    const applicantsSelectedYesValue = applicantsSelectedYes.map(a => a.value)
    if (!_.isEmpty(applicantsSelectedYes)) {
      const filteredList = list.filter(item =>
        !item.applicantName?.applicantType || applicantsSelectedYesValue.includes(item.applicantName.applicantType)
      )

      if (filteredList.length !== list.length) {
        dispatch(setField(listPath, filteredList, false, false, true))
        dispatch(setField(countPath, filteredList.length, false, false, true))
      }
      applicantsSelectedYes.filter(a => !list.some(item => item.applicantName?.applicantType === a.value))
        .forEach(a => {
          dispatch(appendList(listPath,
            {
              ...defaultFields, applicantName: {
                ...defaultFields.applicantName,
                value: applicantFullName(a.value), applicantType: a.value
              }
            },
            countPath))
        })
    } else if (!_.isEmpty(list)) {
      dispatch(setField(listPath, [], false, false, true))
      dispatch(setField(countPath, 0, false, false, true))
    }
  }

  useEffect(() => {
    if ('deliveryDate' in healthQuestion) {
      dispatch(setField(
        `${rootPath}.deliveryDate`,
        {
          ...anyApplicantSelectedYes ? {
            ...requiredField,
            value: healthQuestion.deliveryDate.value,
            maskOptions: DATE_MASK_OPTIONS
          } : optionalField
        }
      ))
    }

    if (healthQuestion.isMedicationInfoRequired && !disableMedication) {
      syncApplicantSpecificList(
        healthQuestion.medications,
        medicationFields,
        `${rootPath}.medications`,
        `${segmentPath}.medicationCount`
      )
    }

    if (healthQuestion.isAdditionalInfoRequired && !disableAdditionalInfo) {
      syncApplicantSpecificList(
        healthQuestion.medicalConditions,
        medicalConditionFields,
        `${rootPath}.medicalConditions`,
        `${segmentPath}.medicalConditionCount`
      )
    }

    // eslint-disable-next-line
  }, [healthQuestion.employee, healthQuestion.spouse, healthQuestion.children, healthQuestion.choices])

  const yesNoQuestionText = (list) => {
    return list ? <>
      <p dangerouslySetInnerHTML={{ __html: healthQuestion.label }} />
      {healthQuestion.detail && <ul>
        {healthQuestion.detail.flat().map(d => <li key={d}>{d}</li>)}
      </ul>}
    </> : <p dangerouslySetInnerHTML={{ __html: healthQuestion.label }} />
  }

  const yesNoQuestion = (list = false) => {
    return <>
      <GridColumn computer={13} tablet={16} mobile={16}>
        <h6 data-testid='yesNo'>{id.replace('q', 'Health Question #')}</h6>
        {yesNoQuestionText(list)}
      </GridColumn>
      <GridColumn computer={3} tablet={16} mobile={16}>
        {applicants.map(a => {
          return <YesNoSelect
            key={a.value}
            id={`${id}-${a.value}`}
            label={a.label}
            path={`${rootPath}.${a.value}`}
          />
        })}
      </GridColumn>
    </>
  }

  const choiceHealthQuestion = () => {
    return <>
      <GridColumn width={16}>
        <h6>{id.replace('q', 'Health Question #')}</h6>
        <p data-testid='choice'>{healthQuestion.label}</p>
      </GridColumn>
      <table className='Table Table--striped'>
        <thead>
        <tr>
          <td />
          {applicants.map(a => {
            return <td key={a.value}>{a.label}</td>
          })}
        </tr>
        </thead>
        <tbody>
        {Object.keys(healthQuestion.choices).map(c => {
          return <tr key={c} className='Table--striped'>
            <td><span aria-label={id} dangerouslySetInnerHTML={{ __html: healthQuestion.choices[c].label }} /></td>
            {applicants.map(a => {
              return <td key={a.value}><RegisteredFormField
                id={`${id}-${a.value}`}
                inputType='checkbox'
                path={`${rootPath}.choices.${c}.${a.value}`}
              /></td>
            })}
          </tr>
        })}
        </tbody>
      </table>
    </>
  }

  const resetApplicantAnswer = (removedItem) => {
    const applicantType = removedItem?.applicantName?.applicantType
    if (!applicantType) return

    if (healthQuestion.type === 'choice') {
      Object.keys(healthQuestion.choices).forEach(c => {
        if (healthQuestion.choices[c][applicantType]?.value) {
          dispatch(setField(`${rootPath}.choices.${c}.${applicantType}.value`, false))
        }
      })
    } else {
      dispatch(setField(`${rootPath}.${applicantType}.value`, 'no'))
    }
  }

  const additionalFields = () => {
    return <>
      {('deliveryDate' in healthQuestion && healthQuestion.deliveryDate.required) &&
      <RegisteredFormField
        label='Anticipated Delivery Date'
        path={`${rootPath}.deliveryDate`}
        id='deliveryDate'
      />}
      {!_.isEmpty(healthQuestion.medications) && <DynamicList
        path={`${rootPath}.medications`}
        defaultObject={medicationFields}
        RowElement={MedicationInputRow}
        disableAppending={disableMedication}
        pathForCount={`${segmentPath}.medicationCount`}
        onRemove={resetApplicantAnswer}
      />}
      {!_.isEmpty(healthQuestion.medicalConditions) && <DynamicList
        path={`${rootPath}.medicalConditions`}
        defaultObject={medicalConditionFields}
        RowElement={HealthQuestionAdditionalInformationRow}
        disableAppending={disableAdditionalInfo}
        pathForCount={`${segmentPath}.medicalConditionCount`}
        onRemove={resetApplicantAnswer}
      />}
    </>
  }

  const renderContent = (mainContent) => {
    return <>
      {mainContent}
      {additionalFields()}
    </>
  }

  const renderHealthQuestion = () => {
    switch (healthQuestion.type) {
      case 'choice':
        return renderContent(choiceHealthQuestion())
      case 'html':
        return renderContent(yesNoQuestion())
      case 'list':
        return renderContent(yesNoQuestion(true))
      default:
        return <></>
    }
  }

  return renderHealthQuestion()
}

HealthQuestion.propTypes = {
  /** A unique DOM Element ID */
  id: PropTypes.string.isRequired,
  /** The selected object from the redux store */
  healthQuestion: PropTypes.object.isRequired,
  /** Redux store object root path */
  rootPath: PropTypes.string.isRequired,
  /** The applicants that can (or must) provide an answer for the question */
  applicants: PropTypes.arrayOf(PropTypes.exact({
    /** The value for the applicant in the redux store */
    value: PropTypes.string,
    /** The label to render for the applicant's health question input */
    label: PropTypes.string
  })),
  /** Whether the maximum amount of medical conditions have been added */
  disableAdditionalInfo: PropTypes.bool,
  /** Whether the maximum amount of medications have been added */
  disableMedication: PropTypes.bool,
  /** The root path of the parent segment containing this question */
  segmentPath: PropTypes.string
}

export default HealthQuestion